import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Sparkles, UserPlus, LogIn, Gift, Calendar, CheckCircle2, X, Eye, EyeOff,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline panel shown on the BookingSuccess page.
 *
 * Flow:
 *  - Debounced email-exists check → toggles between two UIs:
 *    * "Create free account" — name + password form
 *    * "You already have an account" — login CTA
 *  - Successful claim sets the session cookies + calls refreshAuth() + redirects to /dashboard
 *  - Skip link dismisses the panel locally; the customer keeps the booking-id link
 *    in their confirmation email so they can claim later from the login page.
 *
 * Hidden once the user is already authenticated (e.g. reload the success page).
 */
const AccountCreationPanel = ({ booking, onDismiss }) => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [emailExists, setEmailExists] = useState(null);       // null = unchecked, true/false
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const email = booking?.customer_email || '';

  // Check once on mount whether this email already has an account
  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    axios.post(`${API_URL}/api/auth/check-email`, { email }, { withCredentials: true })
      .then((r) => { if (!cancelled) setEmailExists(Boolean(r.data?.exists)); })
      .catch(() => { if (!cancelled) setEmailExists(null); });
    return () => { cancelled = true; };
  }, [email]);

  // Pre-fill name from booking so the customer doesn't retype
  useEffect(() => {
    if (booking?.customer_name) setName(booking.customer_name);
  }, [booking]);

  // If already logged in, offer to link the booking to their account
  useEffect(() => {
    if (!user || user.role !== 'customer' || !booking?.booking_id) return;
    // If the logged-in user IS the customer on the booking, the link call is a no-op
    if ((user.email || '').toLowerCase() !== email.toLowerCase()) return;
    axios.post(`${API_URL}/api/customer/link-booking`,
      { booking_id: booking.booking_id },
      { withCredentials: true },
    ).catch(() => { /* silent — backend is idempotent */ });
  }, [user, booking, email]);

  // Hide when user is already authenticated or has dismissed the panel
  if (dismissed || !booking?.booking_id) return null;
  if (user && user.role === 'customer') return null;

  const handleSkip = () => {
    setDismissed(true);
    onDismiss?.();
    toast('You can create your account anytime from the login page.', { icon: '👍' });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/claim-booking`,
        {
          booking_id: booking.booking_id,
          email,
          password,
          name: name.trim() || undefined,
        },
        { withCredentials: true },
      );
      if (data?.status === 'email_exists') {
        setEmailExists(true);
        toast.info(data.message || 'An account already exists. Please log in.');
        return;
      }
      toast.success('Your account is ready — welcome!');
      await checkAuth?.();
      // Small delay to let the toast flash + cookies propagate
      setTimeout(() => navigate('/my-bookings'), 600);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login', { state: { fromBooking: booking.booking_id, prefillEmail: email } });
  };

  return (
    <AnimatePresence>
      <motion.div
        key="account-creation-panel"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="relative mt-6 overflow-hidden rounded-2xl border-2 p-6 sm:p-7"
        style={{
          background: 'linear-gradient(135deg,#FFF1F4 0%,#FFF8E6 60%,#F0FAFF 100%)',
          borderColor: '#FF3B6B33',
        }}
        data-testid="account-creation-panel"
      >
        {/* Dismiss "X" button for the subtle case when user just wants to close */}
        <button
          type="button"
          onClick={handleSkip}
          aria-label="Dismiss account creation"
          className="absolute top-3 right-3 p-1.5 rounded-full text-[#6B6B7A] hover:bg-white/60 hover:text-[#1E1E24]"
          data-testid="account-panel-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
            <Sparkles className="w-5 h-5 text-[#FF3B6B]" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg sm:text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              🎉 Your party is booked — get more out of it!
            </h3>
            <p className="text-sm text-[#6B6B7A] mt-0.5">
              Create a free account to manage your booking, send invites, and earn rewards.
            </p>
          </div>
        </div>

        {/* Feature chips */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          <FeatureChip icon={Calendar} label="Edit your booking" />
          <FeatureChip icon={UserPlus} label="Digital invitations" />
          <FeatureChip icon={Gift}     label="Earn loyalty points" />
          <FeatureChip icon={CheckCircle2} label="Booking history" />
        </div>

        {emailExists === true ? (
          // Branch A: this email already has an account
          <div className="bg-white rounded-xl p-4 border border-[#E8E5DF]">
            <p className="text-sm text-[#1E1E24]">
              <strong>An account already exists</strong> for <span className="font-mono">{email}</span>.
              Log in to manage this booking alongside your others.
            </p>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Button
                type="button"
                onClick={handleGoToLogin}
                className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
                data-testid="account-panel-login-btn"
              >
                <LogIn className="w-3.5 h-3.5 mr-1" />
                Log in to manage booking
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSkip}
                className="rounded-full border-[#E8E5DF]"
                data-testid="account-panel-skip-btn"
              >
                No thanks, I'll do it later
              </Button>
            </div>
          </div>
        ) : (
          // Branch B: fresh email — show signup form
          <form onSubmit={handleCreate} className="space-y-3" data-testid="account-panel-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="claim-name" className="text-xs font-semibold text-[#1E1E24]">Your name</Label>
                <Input
                  id="claim-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="rounded-xl border-[#E8E5DF] bg-white mt-1"
                  data-testid="account-panel-name"
                />
              </div>
              <div>
                <Label htmlFor="claim-email" className="text-xs font-semibold text-[#1E1E24]">Email</Label>
                <Input
                  id="claim-email"
                  value={email}
                  disabled
                  className="rounded-xl border-[#E8E5DF] bg-white/70 mt-1 text-[#6B6B7A]"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="claim-password" className="text-xs font-semibold text-[#1E1E24]">
                Create a password <span className="font-normal text-[#9a9aa6]">(min 8 characters)</span>
              </Label>
              <div className="relative mt-1">
                <Input
                  id="claim-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                  className="rounded-xl border-[#E8E5DF] bg-white pr-10"
                  data-testid="account-panel-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#6B6B7A] hover:text-[#1E1E24]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
                data-testid="account-panel-create-btn"
              >
                <UserPlus className="w-3.5 h-3.5 mr-1" />
                {submitting ? 'Creating…' : 'Create my free account'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSkip}
                className="rounded-full border-[#E8E5DF]"
                data-testid="account-panel-skip-btn"
              >
                No thanks, I'll do it later
              </Button>
            </div>
            <p className="text-[10px] text-[#9a9aa6] pt-0.5">
              By creating an account you agree to our terms. Your password is encrypted and never shared.
            </p>
          </form>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

const FeatureChip = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1 bg-white/80 border border-white text-[11px] text-[#1E1E24] rounded-full px-2.5 py-1 shadow-sm">
    <Icon className="w-3 h-3 text-[#FF3B6B]" />
    {label}
  </span>
);

export default AccountCreationPanel;
